/* eslint-disable jsx-a11y/no-autofocus */

import React from 'react';
import pt from 'prop-types';

import AppDetails from 'admin/components/app/details';
import AppVersionDetails from 'admin/components/app/version-details';

import { View } from 'components/view';
import AppIcon from 'components/apps/icon';
import Icon from 'components/icon';
import ExternalLink from 'components/link/external';
import Tabs from 'components/tabs';
import { Form, TabView } from 'components/form';
import { SectionGroup } from 'components/section';
import VersionStatus from 'components/apps/version-status';

import {
  getMarketplacePreviewURL,
  getNewPublishableVersionRecord,
} from 'utils/app';

import AppReview from './review';
import AppVersions from './versions';
import AppStagedReleaseSection from './staged-release-section';
import AppNewReleaseSection from './new-release-section';

import 'partners/components/pages/apps/apps.scss';

// A version's review status is set when the release is submitted and kept
// afterwards (approved/rejected/unapproved), so it marks an app that has been
// through review. Comments outlive an unpublish, which clears the status.
function hasAppReview(app) {
  return Boolean(
    app.review_comments?.count > 0 ||
      app.versions?.results?.some(({ review_status }) => review_status),
  );
}

export default class AppView extends React.PureComponent {
  static propTypes = {
    record: pt.object,
    versionRecord: pt.object,
    app: pt.object,
    values: pt.object,
    location: pt.object,
    onSubmitReview: pt.func,
    onPublishRelease: pt.func,
    onUnpublishRelease: pt.func,
    onCreateComment: pt.func,
    onUpdateComment: pt.func,
    onDeleteComment: pt.func,
  };

  onClickUnpublishRelease = (event) => {
    event.preventDefault();

    const { version } = event.currentTarget.dataset;

    this.props.onUnpublishRelease(version);
  };

  renderReleaseSection() {
    const { app, onPublishRelease, onSubmitReview } = this.props;
    const { staged_props } = app;
    const stagedVersion = staged_props?.version;

    if (stagedVersion) {
      return <AppStagedReleaseSection version={stagedVersion} />;
    }

    const newPublishableVersionRecord = getNewPublishableVersionRecord(app);

    if (newPublishableVersionRecord) {
      return (
        <AppNewReleaseSection
          app={app}
          versionRecord={newPublishableVersionRecord}
          onPublish={onPublishRelease}
          onSubmitReview={onSubmitReview}
        />
      );
    }

    return null;
  }

  render() {
    const {
      record,
      app,
      versionRecord,
      values,
      location,
      onCreateComment,
      onUpdateComment,
      onDeleteComment,
    } = this.props;
    const { versions } = app;
    const tab = location.query.tab || 'default';

    return (
      <div className="view-detail-full partner-app-view">
        <Form nested={true} autoFocus={true} values={values}>
          <View
            detail={true}
            uri="/apps"
            sectionTitle="Apps"
            headerImage={<AppIcon app={record} size={60} />}
            headerTitle={app.name}
            headerSubtitle={app.description}
            extraActions={[
              {
                component: (
                  <ExternalLink
                    key="preview-release"
                    to={getMarketplacePreviewURL(app.id)}
                    icon={false}
                  >
                    <span className="with-icon">
                      <Icon fa="external-link" />
                      Preview release
                    </span>
                  </ExternalLink>
                ),
              },
            ]}
          >
            <fieldset className="last-child">
              {this.renderReleaseSection()}

              <div className="view-header-tabs">
                <Tabs
                  items={{
                    default: {
                      label: record.version,
                    },
                    versions: {
                      label: `Versions ${
                        versions?.count > 0 ? `(${versions.count})` : ''
                      }`,
                    },
                    // Nothing to read until a release has been submitted
                    ...(hasAppReview(app) && {
                      review: {
                        label: 'Review',
                      },
                    }),
                  }}
                  location={location}
                  active={tab}
                />
              </div>

              <TabView value={tab} active="default">
                <SectionGroup>
                  <table className="kpi-table">
                    <tbody>
                      <tr>
                        <td>
                          <span className="kpi-label">Status</span>
                          <span className="kpi-value">
                            <div>
                              <VersionStatus
                                app={app}
                                version={versionRecord}
                              />
                            </div>
                          </span>
                        </td>
                        <td>
                          <span className="kpi-label">Supported</span>
                          <span className="kpi-value">
                            <div>{versionRecord?.supported ? 'Yes' : 'No'}</div>
                          </span>
                        </td>
                        <td width="25%">
                          <span className="kpi-label">Installs</span>
                          <span className="kpi-value">
                            <div>{versionRecord?.installs?.count || 0}</div>
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* The same read-only detail tables the super admin shows,
                      without its edit action or its console tooling. Inside the
                      group so the tables get its gap, as they do there. */}
                  <AppVersionDetails
                    key={versionRecord?.version}
                    app={app}
                    record={record}
                    versionRecord={versionRecord}
                    showEditAction={false}
                    showConfigurations={false}
                  />
                </SectionGroup>
              </TabView>

              <TabView value={tab} active="versions">
                <AppVersions
                  app={app}
                  onClickUnpublishRelease={this.onClickUnpublishRelease}
                />
              </TabView>

              <TabView value={tab} active="review">
                <AppReview
                  app={app}
                  onCreateComment={onCreateComment}
                  onUpdateComment={onUpdateComment}
                  onDeleteComment={onDeleteComment}
                />
              </TabView>
            </fieldset>

            <AppDetails app={app} showPayouts={false} />
          </View>
        </Form>
      </div>
    );
  }
}
